// Table.js (Updated)
import React, { useState, useEffect, useRef } from 'react';
import 'react-resizable/css/styles.css';
import './Table.css';
import { TABLE_STATUS_COLORS } from '../../constant/constants';
import { shouldBlinkATable } from '../../services/order-table-base-service';

// (Put getStatusColor function here)
const getStatusColor = (status) => {
  switch (status) {
    case 'Available': return TABLE_STATUS_COLORS.AVAILABLE;
    case 'Reserved': return TABLE_STATUS_COLORS.RESERVED;
    case 'On Dine': return TABLE_STATUS_COLORS.ON_DINE;
    case 'Occupied': return TABLE_STATUS_COLORS.OCCUPIED;
    default: return '#eee';
  }
};

const getResizeRatio = () => {
  return {
    MIN: 0.80,
    MAX: 1.20
  }
}

// 1. Update the Chair component to accept an 'isOccupied' prop
const Chair = ({ style, isOccupied }) => (
  <div
    className={`chair ${isOccupied ? 'occupied' : ''}`}
    style={style}
  >
    <img src="/images/icons/chair.svg" className="w-[15px] h-[20px]"/>
  </div>
);

function DinningTableForAdmin({ data, onTableClick, isSelected }) {
  const { id, x, y, width, height, baseHeight, baseWidth, status, name, capacity, shape, occupiedCount } = data;

  const [isBlink, setIsBlink] = useState(false);

  const tableStyle = {
    width: `${width}px`,
    height: `${height}px`,
    backgroundColor: getStatusColor(status),
    borderRadius: shape === 'Circle' ? '50%' : shape === 'Oval' ? '50px' : '8px',
  };

  // 3. Dynamic Chair Logic
  // This is the tricky part. We can't use hard-coded positions anymore.
  let chairs = [];
  const chairWidth = 15;  // <-- ADD THIS (matches w-[15px])
  const chairHeight = 20; // <-- ADD THIS (matches h-[20px])
  const chairOffset = 15; // How far (px) to place the chair from the table edge

  if (shape === 'Circle') {
    // This logic places chairs in a circle/ellipse
    const numChairs = capacity;
    const radiusX = width / 2;
    const radiusY = height / 2;

    for (let i = 0; i < numChairs; i++) {
      const angle = (i / numChairs) * 2 * Math.PI; // Angle in radians

      // 1. Calculate rotation in degrees
      // We subtract 90 degrees because a 0-degree angle is on the "right" (x-axis),
      // but a 0-degree CSS rotation (your un-rotated chair) faces "up".
      // This adjustment makes the "up" of the chair point "inward".
      const rotation = ((angle * (180 / Math.PI)) - 90) + 180;

      // Calculate position
      const left = (width / 2) + (radiusX + chairOffset) * Math.cos(angle) - (chairWidth / 2);
      const top = (height / 2) + (radiusY + chairOffset) * Math.sin(angle) - (chairHeight / 2);

      chairs.push({ top: `${top}px`, left: `${left}px`, transform: `rotate(${rotation}deg)` });
    }
  } else {
    // --- NEW DYNAMIC LOGIC FOR RECTANGLES/SQUARES ---
    let numTop, numBottom, numLeft, numRight;

    // --- SPECIAL CASE FOR CAPACITY 2 ---
    // This will force 2 chairs to be placed on the left and right (X-axis).
    if (capacity === 2) {
      numTop = 0;
      numBottom = 0;
      numLeft = 1;
      numRight = 1;
    }
    else {
      // 1. Determine how many chairs go on horizontal vs vertical sides
      const horizontalRatio = width / (width + height);
      let numHorizontal = Math.round(capacity * horizontalRatio);
      // Ensure horizontal is an even number for balance
      if (numHorizontal % 2 !== 0 && capacity > 1) {
        numHorizontal = numHorizontal + 1 > capacity ? numHorizontal - 1 : numHorizontal + 1;
      }
      let numVertical = capacity - numHorizontal;
      // 2. Split them between top/bottom and left/right
      numTop = Math.ceil(numHorizontal / 2);
      numBottom = Math.floor(numHorizontal / 2);
      numLeft = Math.ceil(numVertical / 2);
      numRight = Math.floor(numVertical / 2);
    }
    // 3. Create a helper function to place chairs evenly on one side
    const placeChairs = (count, side) => {
      for (let i = 0; i < count; i++) {
        let style = {};
        const positionPercent = (100 / (count + 1)) * (i + 1);

        switch (side) {
          case 'top':
            style = {
              top: `-${chairHeight / 2 + chairOffset}px`,
              left: `${positionPercent}%`,
              transform: `translateX(-50%) rotate(0deg)`
            };
            break;
          case 'bottom':
            style = {
              bottom: `-${chairHeight / 2 + chairOffset}px`,
              left: `${positionPercent}%`,
              transform: `translateX(-50%) rotate(180deg)`
            };
            break;
          case 'left':
            style = {
              left: `-${chairWidth / 2 + chairOffset}px`,
              top: `${positionPercent}%`,
              transform: `translateY(-50%) rotate(270deg)`
            };
            break;
          case 'right':
            style = {
              right: `-${chairWidth / 2 + chairOffset}px`,
              top: `${positionPercent}%`,
              transform: `translateY(-50%) rotate(90deg)`
            };
            break;
          default:
            break;
        }
        chairs.push(style);
      }
    };

    // 4. Place all the chairs
    placeChairs(numTop, 'top');
    placeChairs(numBottom, 'bottom');
    placeChairs(numLeft, 'left');
    placeChairs(numRight, 'right');
  }

  const handleTableClick = () => {
    if (onTableClick) {
      onTableClick(data.id);
    }
  };

  useEffect(() => {
    setIsBlink(shouldBlinkATable(data.activeOrders));
  }, [data.activeOrders])
  
  return (
    <div
      className={`table ${isSelected ? "selected" : ""} ${isBlink ? 'table-blink' : ''}`}
      style={{
        ...tableStyle,
        position: "absolute",
        left: x,
        top: y,
        cursor: "pointer"
      }}
      onClick={handleTableClick}
    >
      {/* Chairs */}
      {chairs.map((style, index) => (
        <Chair key={index} style={style} isOccupied={index < occupiedCount} />
      ))}

      {/* Table Content */}
      <div className="table-content">
        <div className="flex flex-row items-center">
          <img className="w-[20px] h-[12px]" src="/images/icons/daining-table.svg" />
          <strong className="text-[14px] ml-[6px] font-bold">{name}</strong>
        </div>
        <div className="flex flex-row items-center">
          <img className="w-[11px] h-[11px]" src="/images/icons/user-man-svgrepo-com.svg" />
          <strong className="text-[11px] ml-[5px]">{capacity > 9 ? capacity : "0"+capacity}</strong>
        </div>
      </div>
    </div>
  );
}

export default DinningTableForAdmin;
